import React, { useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useAriaLive } from '@ally/use-aria-live';
import { Spinner } from '../../../Spinner';
import { AutoCompleteSelect, baseAutoCompleteA11yProps } from './Selects';
import { useInputSelect } from './useInputSelect';
import { a11y, visual } from '../../strings';
const SelectContainer = styled.span `
  ${({ top, left, width, theme: { colors } }) => `
  position: absolute;
    top: ${top}px;
    left: ${left}px;
    width: ${width}px;
    background: white;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    border-top: 3px solid ${colors.bluesuedeshoes};
    overflow: hidden;
    z-index: 10;

    box-shadow:
      0 2px 2px 0 rgba(0, 0, 0, 0.08),
      0 3px 1px -2px rgba(0, 0, 0, 0.08),
      0 1px 5px 0 rgba(0, 0, 0, 0.16);
  `}
`;
const SpinnerContainer = styled(Spinner) `
  ${({ theme: { colors, space } }) => `
    background: ${colors.white};
    padding: ${space.xs} ${space.md};
    text-align: center;
  `}
`;
const InputSelect = ({ inputWrapperRef, variant, a11yDescription, options, onSelect, onA11yPropsUpdate, inputValue, minSearchChars, maxDisplayedOptions = 5, isLoading, getAltTextSpinner = a11y.getAltTextSpinner, getAriaLiveSelection = a11y.getAriaLiveSelection, getAriaLiveResults = a11y.getAriaLiveResults, noResultsText = visual.noResults, }) => {
    const { announceAssertive } = useAriaLive();
    const { filteredOptions, boundaries, showOptions, showLoading, setShowOptions, focusOnInput, isMounted, } = useInputSelect({
        variant,
        options,
        inputWrapperRef,
        inputValue,
        minSearchChars,
        maxDisplayedOptions,
        isLoading,
        noResultsText,
        getAriaLiveResults,
    });
    const resetA11yProps = useCallback(() => {
        switch (variant) {
            case 'auto-complete':
                onA11yPropsUpdate(baseAutoCompleteA11yProps);
                break;
            default:
                onA11yPropsUpdate({});
        }
    }, [variant, onA11yPropsUpdate]);
    useEffect(() => {
        resetA11yProps();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant]);
    useEffect(() => {
        if (!showOptions && isMounted)
            resetA11yProps();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOptions]);
    const renderSelectList = () => {
        switch (variant) {
            case 'auto-complete':
                return (React.createElement(AutoCompleteSelect, { options: filteredOptions, noResultsText: noResultsText, onSelect: (option) => {
                        onSelect(option);
                        focusOnInput(inputWrapperRef);
                        announceAssertive(getAriaLiveSelection(option.label));
                        setTimeout(() => setShowOptions(false), 0);
                    }, onA11yPropsUpdate: onA11yPropsUpdate }));
            default:
                return null;
        }
    };
    const renderContent = () => {
        if (showLoading) {
            return (React.createElement(SpinnerContainer, { altText: variant === 'auto-complete'
                    ? getAltTextSpinner(a11yDescription)
                    : undefined, size: "small", themeColor: "dark" }));
        }
        if (showOptions) {
            return renderSelectList();
        }
        return null;
    };
    if (!showOptions && !showLoading)
        return null;
    return (React.createElement(SelectContainer, Object.assign({}, boundaries, { "data-testid": "mui-input-select-container" }), renderContent()));
};
export { InputSelect };
//# sourceMappingURL=InputSelect.js.map